import { parse } from 'hub-http/helpers/params';
import { getCurrencyCodeString, isCurrencyCodeString } from 'pricing-pages-api/currency/types';
import { getQueryParams, stringifyQueryParams } from 'pricing-pages-ui-components/utils/queryParamUtils';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { isQueryParamTerm, isQueryParamTermLegacy } from '../typeguards';
// Add type guard
function isValidQueryValue(value) {
  return value !== undefined && value !== null;
}
const useQuery = () => {
  const {
    search
  } = useLocation();
  return useMemo(() => {
    return parse(search.slice(1));
  }, [search]);
};
const useCurrentParam = paramName => {
  const query = useQuery();
  return query[paramName] || null;
};
export const useSetQueryParams = () => {
  const history = useHistory();
  const location = useLocation();
  return useCallback(updatedParams => {
    const filteredParams = Object.fromEntries(Object.entries(updatedParams).filter(([__, value]) => isValidQueryValue(value)));
    const newQueryString = stringifyQueryParams(Object.assign({}, getQueryParams(), filteredParams));
    history.replace({
      pathname: location.pathname,
      search: newQueryString,
      hash: location.hash
    });
  }, [history, location.pathname, location.hash]);
};
export const useQueryParamTerm = () => {
  const termsFromQueryParam = useCurrentParam('term');
  const setQueryParamsHook = useSetQueryParams();
  let queryParamTerm;
  if (isQueryParamTerm(termsFromQueryParam)) {
    queryParamTerm = termsFromQueryParam;
  } else if (isQueryParamTermLegacy(termsFromQueryParam)) {
    queryParamTerm = termsFromQueryParam.toUpperCase();
  } else {
    queryParamTerm = 'ANNUAL';
  }
  const setQueryParamTerm = updatedQueryParamTerm => {
    if (!updatedQueryParamTerm) return;
    const normalizedUpdatedQueryParamTerm = updatedQueryParamTerm.toLowerCase();
    setQueryParamsHook({
      term: normalizedUpdatedQueryParamTerm
    });
  };
  const termLengthQueryParamTerm = queryParamTerm.toUpperCase();
  return {
    queryParamTerm: termLengthQueryParamTerm,
    setQueryParamTerm
  };
};
export const useQueryParamCurrency = () => {
  const currencyFromQueryParam = useCurrentParam('currencyCode');
  const setQueryParamsHook = useSetQueryParams();
  let queryParamCurrency;

  // NOTE: We use isCurrencyCodeString here (not isValidCurrencyCodeForQueryParam) because
  // this is a low-level query param reader that intentionally does NOT validate against
  // available currencies. Callers of this hook are responsible for validating the returned
  // currency code against availableCurrencyCodes before using it. This design allows the
  // query param to be read even if the currency is not currently available, which is useful
  // for error handling and fallback logic.
  if (isCurrencyCodeString(currencyFromQueryParam)) {
    queryParamCurrency = currencyFromQueryParam;
  } else {
    queryParamCurrency = null;
  }
  const setQueryParamCurrency = updatedQueryParamCurrency => {
    if (!updatedQueryParamCurrency) return;

    // Filter out symbol types before setting query param
    const currencyStr = getCurrencyCodeString(updatedQueryParamCurrency);
    if (currencyStr) {
      setQueryParamsHook({
        currencyCode: currencyStr
      });
    }
  };
  return {
    queryParamCurrency,
    setQueryParamCurrency
  };
};